<template>
  <div>
    <ul class="stepper">
      <li
        v-for="(item, index) in items"
        :class="{ stepper__item: true, active: activeStep === index }"
        :key="index"
        @click="handleStepClick(index)"
      >
        <div class="number" :number="index + 1" />
        <span v-if="activeStep === index">{{ item.title }}</span>
      </li>
    </ul>
    <template v-if="useSlots === false">
      <keep-alive>
        <component :is="stepComponent" v-bind="items[activeStep].props" />
      </keep-alive>
    </template>
    <template v-else>
      <template v-for="(item, index) in items" :v-if="false">
        <template v-if="index === activeStep">
          <keep-alive :key="index">
            <slot :name="`step-${index}`"></slot>
          </keep-alive>
        </template>
      </template>
    </template>
    <template v-if="showControls && canGoToPreviousStep">
      <hr />
      <div class="control-buttons-container">
        <base-button class="prev" :disabled="!canGoToPreviousStep" @click="prev">
          ◄ Previous Step
        </base-button>
      </div>
    </template>
  </div>
</template>

<script>
import BaseButton from '@/components/base-button';
export default {
  name: 'BaseStepWizard',
  props: {
    items: {
      type: Array,
      required: true,
      validator: (prop) => {
        return prop.every((item) => {
          return (
            typeof item.title === 'string' &&
            (!item.component || typeof item.component === 'object') &&
            (!item.props || typeof item.props === 'object')
          );
        });
      },
    },
    useSlots: {
      type: Boolean,
      default: false,
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    initialStep: {
      type: Number,
      default: 0,
    },
    maxStep: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      activeStep: this.initialStep,
    };
  },
  components: {
    BaseButton,
  },
  computed: {
    // Dynamically loads the component for the active tab
    stepComponent() {
      const activeStep = this.activeStep;
      const activeItem = this.items[activeStep].component;

      return activeItem;
    },
    canGoToPreviousStep() {
      return this.activeStep > 0;
    },
    canGoToNextStep() {
      return this.activeStep < this.items.length - 1;
    },
  },
  watch: {
    activeStep(currentStep) {
      window.scrollTo(0, 0);
      this.$emit('stepChange', currentStep);
    },
  },
  methods: {
    next() {
      if (this.canGoToNextStep) {
        this.activeStep++;
      }
    },
    prev() {
      if (this.canGoToPreviousStep) {
        this.activeStep--;
      }
    },
    handleStepClick(step) {
      if (step <= this.maxStep) {
        this.activeStep = step;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$stepper-middle-shape: polygon(
  20px 50%,
  0% 0%,
  calc(100% - 20px) 0%,
  100% 50%,
  calc(100% - 20px) 100%,
  0% 100%
);
$stepper-start-shape: polygon(
  0% 0%,
  calc(100% - 20px) 0%,
  100% 50%,
  calc(100% - 20px) 100%,
  0% 100%
);
$stepper-end-shape: polygon(20px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);

$primary-blue: #0088db;
$border-color: #ddd;

.stepper {
  display: flex;
  padding: 0;
  width: 100%;
  list-style: none;
  position: relative;
  padding-left: 20px;
  color: black;
}

.stepper__item {
  display: flex;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  flex: 1;
  padding: 20px 20px 20px 40px;
  background: $border-color;
  margin: 0 0 0 -20px;
  clip-path: $stepper-middle-shape;
  -webkit-clip-path: $stepper-middle-shape;

  &.active {
    flex: 1.5;

    .number {
      background: $primary-blue;
      &::after {
        color: white;
      }
    }
  }
}

.stepper__item::after {
  content: '';
  position: absolute;
  z-index: -1;
  background: white;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  transform: scaleX(0.99) scaleY(0.95);
  clip-path: $stepper-middle-shape;
}

.stepper__item:first-child,
.stepper__item:first-child::after {
  -webkit-clip-path: $stepper-start-shape;
  clip-path: $stepper-start-shape;
}

.stepper__item:last-child,
.stepper__item:last-child::after {
  -webkit-clip-path: $stepper-end-shape;
  clip-path: $stepper-end-shape;
}

.number {
  display: inline-block;
  height: 25px;
  width: 25px;
  border: 2px solid $primary-blue;
  border-radius: 100%;
  margin-right: 10px;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: attr(number);
    position: absolute;
    color: black;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

@media (max-width: 478px) {
  .stepper__item {
    padding: 20px 20px 20px 5px;
  }

  .stepper__item:not(:first-child) {
    padding-left: 25px;
  }

  .stepper__item.active {
    flex: 3;
  }
}

.control-buttons-container {
  display: flex;
  margin: 1rem 0;

  button.next {
    margin-left: auto;
  }
}
</style>
